import React, { useEffect, useRef } from "react";
import { completeOnboarding } from "../api";
import { track } from "../utils/analytics";
import { clearOnboarding } from "../utils/attribution";
import { markGoalReminderShown } from "./DailyGoalReminder";
import { MOTIVATION_LABELS } from "./onboarding/onboardingContent";

/**
 * FirstRunWelcome — full-screen overlay shown once, on a new user's first
 * arrival in the app shell (onboarded_at === null on /api/auth/me/). Covers
 * every arrival path — Stripe return, voucher signup, direct signup — because
 * the flag is server-side truth. Full-screen rather than a modal so the empty
 * topic pane is never glimpsed behind it.
 *
 * Every exit stamps onboarded_at (idempotent endpoint), patches the local
 * user, and clears the staged localStorage attribution.
 */
export default function FirstRunWelcome({ user, onGoQuran, onGoFirstTopic, onDismiss }) {
  const motivation = user?.learning_motivation || "";
  const minutes = user?.daily_goal_minutes || null;
  const goal = MOTIVATION_LABELS[motivation] || "";
  const personalized = !!goal;
  const shownTracked = useRef(false);

  useEffect(() => {
    if (shownTracked.current) return;
    shownTracked.current = true;
    track("welcome-shown", { personalized });
  }, [personalized]);

  const finish = (target, action) => {
    // Best-effort: never trap the user on the welcome because the network hiccuped.
    completeOnboarding().catch(() => {});
    clearOnboarding();
    // The welcome already echoed the goal — don't repeat it as a toast today.
    markGoalReminderShown();
    track("welcome-cta", { target });
    if (action) action();
    onDismiss();
  };

  const quranFirst = motivation === "quran" || motivation === "salah";
  const primaryLabel = quranFirst ? "Start with the Quran" : "Start my first lesson";

  return (
    <div
      className="dark-scope fixed inset-0 z-[70] flex flex-col items-center justify-center px-6 text-center overflow-y-auto"
      style={{ background: "var(--ink)" }}
    >
      <div className="max-w-sm w-full animate-rise py-10">
        <p dir="rtl" lang="ar" className="font-amiri text-4xl text-dt-gold-bright leading-relaxed mb-6 select-none">
          السَّلَامُ عَلَيْكُم
        </p>
        <h1 className="text-2xl font-semibold text-dt-parchment mb-3">
          Welcome{user?.first_name ? `, ${user.first_name}` : ""}
        </h1>
        <p className="text-sm text-dt-parchment-dim mb-8">
          {personalized ? (
            <>
              Your first step toward {goal}
              {minutes ? ` — ${minutes} minutes today` : ""}. However long it
              takes, a little every day gets you there.
            </>
          ) : (
            <>
              Everything here is built around one goal: understanding the
              language of the Quran. A little every day gets you there.
            </>
          )}
        </p>
        <div className="flex flex-col gap-3">
          <button
            type="button"
            onClick={() =>
              finish(quranFirst ? "quran" : "first-topic", quranFirst ? onGoQuran : onGoFirstTopic)
            }
            className="w-full px-4 py-3.5 rounded-xl bg-dt-gold text-dt-ink hover:brightness-105 text-[15px] font-semibold transition-[filter]"
          >
            {primaryLabel}
          </button>
          <button
            type="button"
            onClick={() => finish("explore", null)}
            className="w-full px-4 py-2.5 text-sm text-dt-parchment-dim hover:text-dt-parchment transition-colors"
          >
            Explore on my own
          </button>
        </div>
      </div>
    </div>
  );
}
